import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtDate, todayDMY, toISODate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DebouncedInput from "@/components/DebouncedInput";
import NumberInput from "@/components/NumberInput";
import DateInput from "@/components/DateInput";
import { useWorkerJob } from "@/lib/workerJob";
import { useLeaveVisible } from "@/hooks/useLeaveVisible";
import InstallTip from "@/components/InstallTip";

let lineKey = 0;
const newLine = (project_id = "") => ({ _k: ++lineKey, project_id, stage_id: "", task: "", hours: 0, notes: "", start_time: "", end_time: "" });

// Minutes between HH:MM times; null if either blank
function spanMins(s, e) {
  if (!s || !e) return null;
  const [sh, sm] = s.split(":").map(Number); const [eh, em] = e.split(":").map(Number);
  return (eh * 60 + em) - (sh * 60 + sm);
}
const nowNZ = () => new Intl.DateTimeFormat("en-GB", { timeZone: "Pacific/Auckland", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date());

function TimeField({ value, onChange, label, testid }) {
  return (
    <div className="flex gap-1 items-center">
      <input type="time" className="input flex-1 min-w-0" aria-label={label} title={label} value={value} onChange={e => onChange(e.target.value)} data-testid={testid} />
      <button type="button" className="btn-ghost text-xs px-2" onClick={() => onChange(nowNZ())} data-testid={`${testid}-now`}>Now</button>
    </div>
  );
}

export default function WorkerTimesheets() {
  const { user } = useAuth();
  const leaveOn = useLeaveVisible();
  const [projects, setProjects] = useState([]);
  const [stageMap, setStageMap] = useState({});
  const [list, setList] = useState([]);
  const [date, setDate] = useState(todayDMY());
  const [entries, setEntries] = useState(() => [newLine()]);
  const [breakTaken, setBreakTaken] = useState(true);
  const [job] = useWorkerJob();
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [leave, setLeave] = useState([]);
  const nav = useNavigate();

  async function load(prefill = false) {
    const [p, t] = await Promise.all([api.get("/my/projects"), api.get("/timesheets", { params: { mine: true } })]);
    setProjects(p.data); setList(t.data);
    api.get("/leave/lines", { params: { mine: true } }).then(r => setLeave(r.data)).catch(() => {});
    if (prefill && job && p.data.some(x => x.id === job)) pickProject(0, job);
  }
  useEffect(() => { load(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadStages(pid) {
    if (!pid || stageMap[pid]) return;
    try {
      const [{ data }, prog] = await Promise.all([
        api.get(`/my/projects/${pid}/stages`),
        api.get(`/my/projects/${pid}/programme-tasks`).catch(() => ({ data: { has_programme: false, tasks: [] } })),
      ]);
      setStageMap(m => ({ ...m, [pid]: { ...data, prog: prog.data } }));
    } catch {}
  }

  async function pickProject(i, pid) {
    const entry = { ...entries[i], project_id: pid, stage_id: "", task: "", task_id: undefined, programme_item_id: "" };
    setEntries(es => { const c = [...es]; c[i] = entry; return c; });
    loadStages(pid);
  }

  function startEdit(t) {
    setErr(""); setOk("");
    setEditId(t.id); setDate(fmtDate(t.work_date)); setBreakTaken(!!t.break_taken);
    setEntries((t.entries || []).map(e => ({ ...newLine(e.project_id), stage_id: e.stage_id || "", task: e.task || "", task_id: e.task_id || undefined,
      programme_item_id: e.programme_item_id || "", hours: e.hours || 0, notes: e.notes || "", start_time: e.start_time || "", end_time: e.end_time || "" })));
    [...new Set((t.entries || []).map(e => e.project_id))].forEach(loadStages);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function cancelEdit() {
    setEditId(null); setDate(todayDMY()); setBreakTaken(true);
    setEntries([newLine(projects.some(x => x.id === job) ? job : "")]);
  }

  async function removeTs(t) {
    if (!window.confirm(`Delete your timesheet for ${fmtDate(t.work_date)}?`)) return;
    try { await api.delete(`/timesheets/${t.id}`); if (editId === t.id) cancelEdit(); setOk("Timesheet deleted."); load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Couldn't delete"); }
  }

  function setEntry(i, k, v) {
    setEntries(es => { const c = [...es]; c[i] = { ...c[i], [k]: v }; return c; });
  }

  function setTime(i, k, v) {
    setEntries(es => {
      const c = [...es]; const en = { ...c[i], [k]: v };
      const m = spanMins(en.start_time, en.end_time);
      if (m !== null && m >= 0) en.hours = Math.round(m / 15) / 4;
      c[i] = en; return c;
    });
  }

  function removeLine(i) {
    setEntries(es => es.filter((_, k) => k !== i));
  }

  async function submit(e) {
    e.preventDefault();
    setErr(""); setOk("");
    const filled = entries.filter(en => en.project_id || (parseFloat(en.hours) || 0) || (en.notes || "").trim() || en.start_time || en.end_time);
    const missing = entries.findIndex(en => !en.project_id && ((parseFloat(en.hours) || 0) || (en.notes || "").trim() || en.start_time || en.end_time));
    if (missing >= 0) { setErr(`Pick a project for line ${missing + 1}`); return; }
    const badTime = entries.findIndex(en => (spanMins(en.start_time, en.end_time) ?? 0) < 0);
    if (badTime >= 0) { setErr(`End time is before start time (line ${badTime + 1})`); return; }
    if (!filled.length) { setErr("Add at least one line with a project and hours"); return; }
    setBusy(true);
    try {
      const payload = { work_date: toISODate(date), entries: filled.map(({ _k, start_time, end_time, ...rest }) => ({ ...rest, start_time: start_time || null, end_time: end_time || null })), break_taken: breakTaken };
      if (editId) await api.put(`/timesheets/${editId}`, payload); else await api.post("/timesheets", payload);
      setEntries([newLine(projects.some(x => x.id === job) ? job : "")]);
      setOk(editId ? "Timesheet updated." : "Timesheet submitted.");
      setEditId(null);
      load();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Couldn't submit the timesheet. Please check the lines and try again.");
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-4" data-testid="worker-timesheets">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h1 className="text-2xl">My Timesheets</h1>
        {leaveOn && <button type="button" className="btn-ghost text-sm px-4 py-2" onClick={() => nav("/my/leave")} data-testid="btn-request-leave-ts">Request leave</button>}
      </div>
      <div className="mb-3"><InstallTip /></div>

      <form onSubmit={submit} className="card space-y-3">
        <h2 className="text-silver" data-testid="ts-form-title">{editId ? "Edit timesheet" : "Submit timesheet"}</h2>
        <label className="text-sm">Date (DD/MM/YYYY)
          <DateInput data-testid="ts-date" className="input" format="dmy" value={date} onChange={e=>setDate(e.target.value)}/></label>
        {entries.map((en, i) => {
          const stages = stageMap[en.project_id]?.stages || [];
          const tasks = stageMap[en.project_id]?.tasks || [];
          const filteredTasks = en.stage_id ? tasks.filter(t => t.stage_id === en.stage_id) : tasks;
          const prog = stageMap[en.project_id]?.prog;
          const mins = spanMins(en.start_time, en.end_time);
          const timed = mins !== null && mins >= 0;
          return (
            <div key={en._k} className="space-y-2" data-testid={`ts-entry-${i}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <select className="input" data-testid={`ts-project-${i}`} value={en.project_id} onChange={e=>pickProject(i, e.target.value)}>
                <option value="">— Project —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select className="input" data-testid={`ts-stage-${i}`} value={en.stage_id} onChange={e=>setEntry(i,"stage_id",e.target.value)}>
                <option value="">— Stage (optional) —</option>
                {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {prog?.has_programme ? (
                <select className="input" data-testid={`ts-task-${i}`} value={en.programme_item_id || ""} onChange={e=>{ const t=prog.tasks.find(t=>t.id===e.target.value); setEntries(es=>{const c=[...es]; c[i]={...c[i], programme_item_id: t?.id || "", task: t?.name || "", task_id: undefined}; return c;}); }}>
                  <option value="">— Task (optional) —</option>
                  {prog.tasks.map(t => <option key={t.id} value={t.id}>{t.name}{t.today ? " (today)" : ""}</option>)}
                </select>
              ) : (
              <select className="input" data-testid={`ts-task-${i}`} value={en.task} onChange={e=>{ const t=filteredTasks.find(t=>t.name===e.target.value); setEntries(es=>{const c=[...es]; c[i]={...c[i], task:e.target.value, task_id: t?.id}; return c;}); }}>
                <option value="">— Task (optional) —</option>
                {filteredTasks.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_8rem_2fr_auto] gap-2 items-start">
              <TimeField label="Start time" value={en.start_time} onChange={v => setTime(i, "start_time", v)} testid={`ts-start-${i}`} />
              <TimeField label="End time" value={en.end_time} onChange={v => setTime(i, "end_time", v)} testid={`ts-end-${i}`} />
              <NumberInput data-testid={`ts-hours-${i}`} className="input" step="0.25" placeholder="Hours" disabled={timed}
                              title={timed ? "Worked out from start and end time" : "Hours"}
                              value={en.hours} onChange={e=>setEntry(i,"hours",parseFloat(e.target.value||0))}/>
              <DebouncedInput className="input" data-testid={`ts-notes-${i}`} placeholder="Notes" value={en.notes} onChange={v=>setEntry(i,"notes",v)}/>
              {entries.length > 1 ? (
                <button type="button" className="btn-ghost text-sm px-3" onClick={()=>removeLine(i)} aria-label={`Remove line ${i + 1}`} data-testid={`ts-remove-${i}`}>✕ Remove</button>
              ) : <span className="hidden md:block" />}
            </div>
            {mins !== null && mins < 0 && <div className="text-sm text-red-400" data-testid={`ts-time-error-${i}`}>End time is before start time</div>}
            {timed && <div className="text-xs opacity-70" data-testid={`ts-calc-${i}`}>{en.start_time}–{en.end_time} = {(Math.round(mins / 15) / 4).toFixed(2)} h</div>}
            </div>
          );
        })}
        <button type="button" className="btn-ghost text-sm" data-testid="ts-add-line" onClick={()=>setEntries(es=>[...es,newLine()])}>+ Add line</button>
        <label className="text-sm flex items-center gap-2" data-testid="ts-break">
          <input type="checkbox" checked={breakTaken} onChange={e=>setBreakTaken(e.target.checked)}/>
          Unpaid 30 min break taken
        </label>
        {err && <div className="text-sm text-red-400" role="alert" data-testid="ts-error">{err}</div>}
        {ok && <div className="text-sm text-silver" data-testid="ts-success">{ok}</div>}
        <div className="flex gap-2 flex-wrap">
          <button className="btn-silver" disabled={busy} data-testid="btn-submit-timesheet">{busy ? "Saving…" : editId ? "Save changes" : "Submit timesheet"}</button>
          {editId && <button type="button" className="btn-ghost" onClick={cancelEdit} data-testid="btn-cancel-edit">Cancel edit</button>}
        </div>
      </form>

      <div className="card mt-4">
        <h2 className="text-silver mb-2">My recent timesheets</h2>
        {list.length === 0 && <div className="opacity-50 text-sm">No timesheets yet.</div>}
        <div className="space-y-2">{mergeLeave(list, leave).map(t => t.leave
          ? <div key={`lv-${t.date}-${t.type}`} className="border border-green-900 rounded p-3 flex justify-between text-sm" data-testid={`my-leave-line-${t.date}`}>
              <span>{t.type.replace(" leave", " Leave")} {fmtDate(t.date).slice(0, 5)}</span><span>{t.hours} h · Approved</span></div>
          : <MyTsRow key={t.id} t={t} editing={editId === t.id} onEdit={startEdit} onDelete={removeTs} />)}</div>
      </div>
    </div>
  );
}

function mergeLeave(list, leave) {
  return [...list, ...leave.map(l => ({ ...l, leave: true, work_date: l.date }))].sort((a, b) => (b.work_date || "").localeCompare(a.work_date || ""));
}

function MyTsRow({ t, editing, onEdit, onDelete }) {
  const locked = t.status === "Accepted" || t.status === "Paid";
  const sum = k => (t.entries || []).reduce((s, e) => s + (parseFloat(e[k]) || 0), 0).toFixed(2);
  return (
    <div className={`border rounded p-3 ${editing ? "border-silver" : "border-white/10"}`} data-testid={`my-ts-${t.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-medium">{fmtDate(t.work_date)}</div>
        <span className="badge badge-grey" data-testid={`my-ts-status-${t.id}`}>{t.status === "Accepted" ? "Approved" : t.status}</span>
      </div>
      <div className="text-xs opacity-70 mt-1">
        {(t.entries || []).map((e, i) => <div key={i}>{e.project_name || "—"}{e.task ? ` · ${e.task}` : ""} · {e.start_time && e.end_time ? `${e.start_time}–${e.end_time} · ` : ""}{e.hours} h</div>)}
      </div>
      <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
        <div className="text-sm">Entered {sum("hours")} h · Paid {sum("paid_hours")} h</div>
        {locked ? <span className="text-xs opacity-60" data-testid={`my-ts-locked-${t.id}`}>Locked</span> : (
          <div className="flex gap-2">
            <button type="button" className="btn-ghost text-sm px-4 py-2" onClick={() => onEdit(t)} data-testid={`my-ts-edit-${t.id}`}>Edit</button>
            <button type="button" className="btn-ghost text-sm px-4 py-2 text-red-400" onClick={() => onDelete(t)} data-testid={`my-ts-delete-${t.id}`}>Delete</button>
          </div>
        )}
      </div>
    </div>
  );
}
